import { useEffect, useMemo } from "react";
import { useAppDispatch, useAppSelector } from "@/app/store";
import { useRolesList, useTenantList, useFeaturesList } from "@/hooks";
import { setRolesList, setTenantsList, setFeaturesList } from "@/features/auth";
import { isEqual } from "lodash";

/**
 * Hook to sync roles, tenants, and subscription features into Redux auth state once.
 */
export const usePermissionsSync = () => {
  const dispatch = useAppDispatch();
  const userRoleKey = useAppSelector(
    (state) => state.auth.user?.currentRoleKey,
  );
  const userTenantId = useAppSelector(
    (state) => state.auth.user?.currentTenantId,
  );

  const currentRolesList = useAppSelector((state) => state.auth.rolesList);
  const currentTenantsList = useAppSelector((state) => state.auth.tenantsList);
  const currentFeaturesList = useAppSelector(
    (state) => state.auth.featuresList,
  );

  const rolesProps = useMemo(() => ({ role: userRoleKey }), [userRoleKey]);
  const { rolesList } = useRolesList(rolesProps);

  const tenantProps = useMemo(
    () => ({ tenantId: userTenantId }),
    [userTenantId],
  );
  const { tenantsList } = useTenantList(tenantProps);

  const subscriptionPlanKey = tenantsList?.[0]?.subscriptionPlan || "";
  const featuresProps = useMemo(
    () => ({ subscriptionPlanKey }),
    [subscriptionPlanKey],
  );
  const { featuresList } = useFeaturesList(featuresProps);

  useEffect(() => {
    if (
      rolesList &&
      rolesList.length > 0 &&
      !isEqual(rolesList, currentRolesList)
    ) {
      dispatch(setRolesList(rolesList));
    }
  }, [rolesList, currentRolesList, dispatch]);

  useEffect(() => {
    if (
      tenantsList &&
      tenantsList.length > 0 &&
      !isEqual(tenantsList, currentTenantsList)
    ) {
      dispatch(setTenantsList(tenantsList));
    }
  }, [tenantsList, currentTenantsList, dispatch]);

  useEffect(() => {
    if (
      featuresList &&
      featuresList.length > 0 &&
      !isEqual(featuresList, currentFeaturesList)
    ) {
      dispatch(setFeaturesList(featuresList));
    }
  }, [featuresList, currentFeaturesList, dispatch]);
};
